import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Modal, Table, useToast, type Column } from 'grabbitbot-legacy-ui'
import { tradingClient } from '../../api'
import type { CoinConfig, PossibleCoinList, QuoteAsset, SpotDefaultConfig, Strategy, Tag } from '../../api/tradingTypes'
import { CoinDetailsPopup } from './CoinDetailsPopup'
import { CoinSettingsPopup } from './CoinSettingsPopup'
import { QuoteAssetSelector } from './QuoteAssetSelector'
import { BATCH_FIELDS, buildBatchModifyBody } from './coinSettingsForm'
import {
  BUCKET_ORDER,
  MAX_MOP_COUNTS,
  TRADING_MODE_BUCKETS,
  isNotTrading,
  minBalanceLabel,
  orderByBucket,
  tradingModeLabel,
} from './coinConfigLabels'
import { getLastQuoteAsset, setLastQuoteAsset } from './quoteAssetStorage'
import { AUTO_BUY_MODES, OFF_MODE_TRIGGERS, PRIMARY_USD_ASSET, bidConstantName, isUsd } from './tradingConstants'
import { failureText } from '../../api/errorMessages'

type PairType = 'NCP' | 'RCP' | 'VCP'

function num(value: unknown): number {
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}

/** Batch-modify command labels (legacy modal's "add command" buttons). */
const BATCH_LABELS: Record<(typeof BATCH_FIELDS)[number], string> = {
  tradingMode: 'Trading Mode',
  bid: 'Bid',
  grid: 'Grid',
  lockOnOffMode: 'Lock Off Trading Mode',
  maxMopCount: 'Max MOP count',
  offModeTrigger: 'Off handler',
  offHandlerRunOnNonSameStrategyPositions: 'Run off handler on non-current strategy MOPs too',
  autoBuyMode: 'Auto Buy Mode',
  autoBuyAmount: 'Auto Buy Amount',
}

/**
 * Equivalent of legacy `V3CoinListSpotComponent` (route
 * `:exchangeId/:tradingType/coins`): the user's coin pairs on one exchange —
 * own ("Custom coins") and strategy-subscribed, grouped by strategy — with
 * the quote-asset/text/tag filters, add coin pair (normal, reverse, virtual),
 * tags (create, rename, add to/remove from a pair, bulk modify by tag) and
 * the coin settings / details popups.
 *
 * Not in this batch (port plan B3 statistics): the per-coin inline profit
 * chart and the statistics resolution/days picker — omitted, not stubbed.
 * Margin is not rewritten (owner 2026-09-26): `tradingType != spot` shows a
 * notice instead of the list.
 */
export function CoinsPage() {
  const { exchangeId = '', tradingType = 'spot' } = useParams<{ exchangeId: string; tradingType: string }>()
  if (tradingType !== 'spot') {
    return (
      <div className="portal-card">
        <h1>Coins</h1>
        <p className="portal-notice portal-notice--warning">Margin trading is not supported.</p>
      </div>
    )
  }
  return <SpotCoinList key={exchangeId} exchangeId={exchangeId} />
}

function SpotCoinList({ exchangeId }: { exchangeId: string }) {
  const { showToast } = useToast()
  const [coin2, setCoin2State] = useState(() => getLastQuoteAsset(true))
  const [showStopped, setShowStopped] = useState(false)
  const [reloadToken, setReloadToken] = useState(0)
  const [coinConfigs, setCoinConfigs] = useState<CoinConfig[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<unknown>(null)
  const [possibleCoins, setPossibleCoins] = useState<PossibleCoinList | null>(null)
  const [defaultConfig, setDefaultConfig] = useState<SpotDefaultConfig | null>(null)
  const [quoteAssets, setQuoteAssets] = useState<QuoteAsset[]>([])
  const [tags, setTags] = useState<Tag[]>([])
  const [selectedTagId, setSelectedTagId] = useState<string | null>(null)
  const [strategiesById, setStrategiesById] = useState<Record<string, Strategy>>({})
  const [coinFilter, setCoinFilter] = useState('')
  const [selectedCoin, setSelectedCoin] = useState<CoinConfig | null>(null)
  const [detailsCoin, setDetailsCoin] = useState<CoinConfig | null>(null)
  const [busy, setBusy] = useState(false)
  const [tagModal, setTagModal] = useState<{ mode: 'add' | 'edit'; tag: Partial<Tag> } | null>(null)
  const [batchOpen, setBatchOpen] = useState(false)

  const reload = useCallback(() => setReloadToken((n) => n + 1), [])
  const setCoin2 = useCallback((value: string) => {
    setLastQuoteAsset(value)
    setCoin2State(value)
  }, [])

  // Legacy `refreshData()`: coin configs, possible coins, default config and
  // tags, all for the route's exchange; `showStopped=1` only when asked.
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    tradingClient
      .searchCoinConfigs(exchangeId, showStopped ? { showStopped: 1 } : undefined)
      .then((list) => {
        if (cancelled) return
        setCoinConfigs(list)
        setLoadError(null)
        const ids = [...new Set(list.map((c) => c.strategyId).filter((id): id is string => Boolean(id)))]
        if (ids.length > 0) {
          tradingClient
            .searchStrategiesByIds(ids)
            .then((strategies) => {
              if (cancelled) return
              const map: Record<string, Strategy> = {}
              for (const s of Array.isArray(strategies) ? strategies : []) map[s.id] = s
              setStrategiesById(map)
            })
            .catch(() => undefined)
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) setLoadError(err ?? true)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    tradingClient
      .searchPossibleCoins(exchangeId)
      .then((result) => !cancelled && setPossibleCoins(result))
      .catch(() => undefined)
    tradingClient
      .viewDefaultConfig(exchangeId)
      .then((result) => !cancelled && setDefaultConfig(result))
      .catch(() => undefined)
    tradingClient
      .searchQuoteAssets(exchangeId)
      .then((result) => !cancelled && setQuoteAssets(Array.isArray(result) ? result : []))
      .catch(() => undefined)
    tradingClient
      .searchTags(exchangeId)
      .then((result) => !cancelled && setTags(result))
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [exchangeId, showStopped, reloadToken])

  const selectedTag = useMemo(() => tags.find((t) => t.id === selectedTagId) ?? null, [tags, selectedTagId])
  const handledPairs = useMemo(() => new Set((coinConfigs ?? []).map((c) => c.coin1 + c.coin2)), [coinConfigs])

  const visible = useCallback(
    (c: CoinConfig) =>
      (coin2 === '-' || coin2 === c.coin2) &&
      (coinFilter === '' || (c.coin1 + c.coin2).includes(coinFilter.toUpperCase())) &&
      (!selectedTag || (c.tags ?? []).includes(selectedTag.id)),
    [coin2, coinFilter, selectedTag],
  )

  const customCoins = useMemo(() => orderByBucket((coinConfigs ?? []).filter((c) => !c.strategyId && visible(c))), [coinConfigs, visible])
  const strategyGroups = useMemo(() => {
    const order: string[] = []
    const groups: Record<string, CoinConfig[]> = {}
    for (const c of coinConfigs ?? []) {
      if (!c.strategyId) continue
      if (!groups[c.strategyId]) {
        groups[c.strategyId] = []
        order.push(c.strategyId)
      }
      if (visible(c)) groups[c.strategyId]!.push(c)
    }
    return order.map((id) => ({ id, rows: orderByBucket(groups[id] ?? []) }))
  }, [coinConfigs, visible])

  async function run(action: () => Promise<unknown>, failure: string) {
    setBusy(true)
    try {
      await action()
      reload()
      return true
    } catch (err) {
      showToast(failureText(err, failure), 'error')
      return false
    } finally {
      setBusy(false)
    }
  }

  const addTagToCoin = (c: CoinConfig, tagId: string) =>
    run(() => tradingClient.addTagToCoinConfig(exchangeId, c.coin1, c.coin2, tagId), 'Could not add the tag')
  const removeTagFromCoin = (c: CoinConfig, tag: Tag) => {
    // Legacy asked with a native confirm('Are you sure?'); the viewer blocks
    // dialogs, so the chip's remove button asks inline instead (see TagChip).
    return run(() => tradingClient.removeTagFromCoinConfig(exchangeId, c.coin1, c.coin2, tag.id), 'Could not remove the tag')
  }

  const columns: Column<CoinConfig>[] = [
    {
      key: 'coinPair',
      label: 'Coinpair',
      align: 'left',
      sortValue: (c) => c.coin1 + c.coin2,
      format: (_v, c) => (
        <span className="portal-inline-actions">
          <Link to={`/${exchangeId}/spot/coins/${c.coin1}/${c.coin2}`}>
            {c.coin1}
            {c.coin2}
            {c.preferences?.type === 'RCP' ? ' - R' : c.preferences?.type === 'VCP' ? ' - V' : ''}
          </Link>
          <button type="button" aria-label={`Details of ${c.coin1}${c.coin2}`} disabled={loading} onClick={() => setDetailsCoin(c)}>
            ⓘ
          </button>
          {num(c.mirrorTrading) > 0 && (
            <span title="MirrorTrading is active" aria-label="MirrorTrading is active">
              ⇄
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'minBalance',
      label: 'Min balance',
      align: 'right',
      numeric: true,
      sortValue: (c) => (num(c.effective?.gridUp) ? (45 / num(c.effective?.gridUp)) * num(c.effective?.bid) : 0),
      format: (_v, c) => minBalanceLabel(c),
    },
    {
      key: 'bid',
      label: <span title="The amount of a trade">Bid</span>,
      align: 'right',
      numeric: true,
      sortValue: (c) => num(c.effective?.bid),
      format: (_v, c) => (
        <>
          {String(c.effective?.bid ?? '')} {c.coin2}
          {c.bidConstant && <span className="portal-muted-small">{c.bidConstant}</span>}
        </>
      ),
    },
    {
      key: 'grid',
      label: <span title="Trade intensity">Grid</span>,
      align: 'right',
      numeric: true,
      sortValue: (c) => num(c.effective?.gridUp),
      format: (_v, c) => {
        const up = c.effective?.gridUp
        const down = c.effective?.gridDown
        return (
          <>
            {String(up) === String(down) ? (
              <span title="UP and DOWN">{String(up ?? '')}% ⇅</span>
            ) : (
              <span>
                <span title="UP">{String(up ?? '')}% ↑</span> / <span title="DOWN">{String(down ?? '')}% ↓</span>
              </span>
            )}
            {c.gridUpConstant && c.gridUpConstant === c.gridDownConstant && (
              <span className="portal-muted-small">{c.gridUpConstant} ⇅</span>
            )}
            {c.gridUpConstant !== c.gridDownConstant && (
              <>
                {c.gridUpConstant && <span className="portal-muted-small">{c.gridUpConstant} ↑</span>}
                {c.gridDownConstant && <span className="portal-muted-small">{c.gridDownConstant} ↓</span>}
              </>
            )}
          </>
        )
      },
    },
    {
      key: 'tradingMode',
      label: 'Trading mode',
      align: 'left',
      sortValue: (c) => BUCKET_ORDER.indexOf(TRADING_MODE_BUCKETS[String(num(c.tradingMode))] ?? 'off'),
      format: (_v, c) => tradingModeLabel(c),
    },
    {
      key: 'tags',
      label: 'Tags',
      align: 'left',
      sortable: false,
      format: (_v, c) => (
        <TagCell
          coin={c}
          tags={tags}
          disabled={busy}
          onSelectTag={(tag) => setSelectedTagId(tag.id)}
          onAdd={(tagId) => addTagToCoin(c, tagId)}
          onRemove={(tag) => removeTagFromCoin(c, tag)}
        />
      ),
    },
    {
      key: 'actions',
      label: '',
      sortable: false,
      format: (_v, c) =>
        num(c.tradingMode) !== -100 && (
          <button type="button" disabled={loading} onClick={() => setSelectedCoin(c)}>
            Settings
          </button>
        ),
    },
  ]

  return (
    <div>
      <div className="portal-page-title">
        <h1>Coins</h1>
        <QuoteAssetSelector value={coin2} quoteAssets={quoteAssets} onChange={setCoin2} anyValueEnabled />
      </div>
      <div className="portal-card">
        <div className="portal-tabs" role="tablist" aria-label="Tags">
          <button
            type="button"
            role="tab"
            aria-selected={!selectedTag}
            className={!selectedTag ? 'portal-tab portal-tab--active' : 'portal-tab'}
            onClick={() => setSelectedTagId(null)}
          >
            All coins
          </button>
          {tags.map((tag) => (
            <button
              key={tag.id}
              type="button"
              role="tab"
              aria-selected={selectedTag?.id === tag.id}
              className={selectedTag?.id === tag.id ? 'portal-tab portal-tab--active' : 'portal-tab'}
              onClick={() => setSelectedTagId(tag.id)}
            >
              {tag.name}
            </button>
          ))}
          <button type="button" className="portal-tab" onClick={() => setTagModal({ mode: 'add', tag: { name: '', description: '' } })}>
            + add tag
          </button>
        </div>

        {loading && <p>Loading…</p>}
        {loadError !== null && <p role="alert">{failureText(loadError, 'Could not load your coin configs')}</p>}

        {coinConfigs && (
          <div style={{ opacity: loading ? 0.5 : 1 }}>
            <div className="portal-grid-2">
              <div>
                <input
                  type="search"
                  placeholder="search coins"
                  aria-label="Search coins"
                  value={coinFilter}
                  onChange={(e) => setCoinFilter(e.target.value)}
                />
                <label className="portal-inline-actions">
                  <input
                    type="checkbox"
                    checked={showStopped}
                    onChange={() => setShowStopped((v) => !v)}
                  />
                  show Stopped coins too
                </label>
              </div>
              {!selectedTag ? (
                <AddCoinWidget
                  possibleCoins={possibleCoins}
                  handledPairs={handledPairs}
                  coin2={coin2}
                  onCoin2Change={setCoin2}
                  busy={busy}
                  onAdd={async (c1, c2) => {
                    if (handledPairs.has(c1 + c2)) {
                      showToast('You already have this coin pair in your config!', 'error')
                      return
                    }
                    const ok = await run(() => tradingClient.modifyCoinConfig(exchangeId, c1, c2, {}), 'Could not add the coin pair')
                    if (ok) setCoin2(c2)
                  }}
                />
              ) : (
                <div className="portal-notice portal-notice--info">
                  <strong>{selectedTag.name}</strong>{' '}
                  <button type="button" aria-label="Edit tag" onClick={() => setTagModal({ mode: 'edit', tag: { ...selectedTag } })}>
                    ✎
                  </button>
                  {selectedTag.description && <p>{selectedTag.description}</p>}
                  <span className="portal-muted-small">ID: {selectedTag.id}</span>
                </div>
              )}
            </div>

            {selectedTag && (
              <AddCoinToTag
                coins={coinConfigs}
                busy={busy}
                onAdd={(c) => addTagToCoin(c, selectedTag.id)}
                onBatch={() => setBatchOpen(true)}
              />
            )}

            <h2>Custom coins</h2>
            <div className="portal-table-scroll">
              <Table
                columns={columns}
                rows={customCoins}
                rowKey={(c) => c.coin1 + c.coin2}
                initialSort={null}
                rowClassName={(c) => (isNotTrading(c) ? 'portal-row--not-trading' : undefined)}
              />
            </div>

            {strategyGroups.length > 0 && (
              <>
                <h2>Strategies</h2>
                {strategyGroups.map((group) => (
                  <section key={group.id} aria-label={`Strategy ${group.id}`}>
                    <h3>
                      <Link to={`/strategy/spot/${group.id}`}>{strategiesById[group.id]?.realPublicName ?? group.id}</Link>
                    </h3>
                    <div className="portal-table-scroll">
                      <Table
                        columns={columns}
                        rows={group.rows}
                        rowKey={(c) => c.coin1 + c.coin2}
                        initialSort={null}
                        rowClassName={(c) => (isNotTrading(c) ? 'portal-row--not-trading' : undefined)}
                      />
                    </div>
                  </section>
                ))}
              </>
            )}
          </div>
        )}
      </div>

      <CoinDetailsPopup coinConfig={detailsCoin} onClose={() => setDetailsCoin(null)} />
      <CoinSettingsPopup
        coinConfig={selectedCoin ? { ...selectedCoin, exchangeId: selectedCoin.exchangeId || exchangeId } : null}
        onClose={(refresh) => {
          setSelectedCoin(null)
          if (refresh) reload()
        }}
      />

      {tagModal && (
        <TagModal
          mode={tagModal.mode}
          initial={tagModal.tag}
          busy={busy}
          onCancel={() => setTagModal(null)}
          onSave={async (tag) => {
            const ok = await run(
              () =>
                tagModal.mode === 'add'
                  ? tradingClient.createTag(exchangeId, { name: tag.name ?? '', description: tag.description ?? '' })
                  : tradingClient.modifyTag(exchangeId, tag.id ?? '', { ...tag }),
              'Could not save the tag',
            )
            if (ok) setTagModal(null)
          }}
        />
      )}

      {batchOpen && selectedTag && (
        <BatchModifyModal
          tag={selectedTag}
          defaultConfig={defaultConfig}
          busy={busy}
          onCancel={() => setBatchOpen(false)}
          onSave={async (body) => {
            const ok = await run(() => tradingClient.modifyCoinConfigByTag(exchangeId, selectedTag.id, body), 'Could not modify the coins')
            if (ok) setBatchOpen(false)
          }}
        />
      )}
    </div>
  )
}

function TagCell({
  coin,
  tags,
  disabled,
  onSelectTag,
  onAdd,
  onRemove,
}: {
  coin: CoinConfig
  tags: Tag[]
  disabled: boolean
  onSelectTag: (tag: Tag) => void
  onAdd: (tagId: string) => void
  onRemove: (tag: Tag) => void
}) {
  const own = coin.tags ?? []
  const [confirming, setConfirming] = useState<string | null>(null)
  const available = tags.filter((t) => !own.includes(t.id))
  return (
    <div>
      <ul className="portal-chip-list">
        {tags
          .filter((t) => own.includes(t.id))
          .map((tag) => (
            <li key={tag.id} className="portal-chip">
              <button type="button" onClick={() => onSelectTag(tag)}>
                {tag.name}
              </button>
              {confirming === tag.id ? (
                <>
                  <span>Are you sure?</span>
                  <button type="button" disabled={disabled} onClick={() => onRemove(tag)}>
                    Yes
                  </button>
                  <button type="button" onClick={() => setConfirming(null)}>
                    No
                  </button>
                </>
              ) : (
                <button type="button" aria-label={`Remove tag ${tag.name}`} onClick={() => setConfirming(tag.id)}>
                  ✕
                </button>
              )}
            </li>
          ))}
      </ul>
      {available.length > 0 && (
        <select
          aria-label={`Add tag to ${coin.coin1}${coin.coin2}`}
          value=""
          disabled={disabled}
          onChange={(e) => e.target.value && onAdd(e.target.value)}
        >
          <option value="">+ add tag</option>
          {available.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      )}
    </div>
  )
}

function AddCoinWidget({
  possibleCoins,
  handledPairs,
  coin2,
  onCoin2Change,
  busy,
  onAdd,
}: {
  possibleCoins: PossibleCoinList | null
  handledPairs: Set<string>
  coin2: string
  onCoin2Change: (coin2: string) => void
  busy: boolean
  onAdd: (coin1: string, coin2: string) => void
}) {
  const [typeState, setType] = useState<PairType>('NCP')
  const [filter, setFilter] = useState('')
  const [newCoin, setNewCoin] = useState('')
  const [vcp1, setVcp1] = useState('')
  const [vcp2, setVcp2] = useState('')

  if (!possibleCoins) return <p>Loading…</p>
  const coins = possibleCoins.coins ?? []
  // The tradeable coin-pair allow-list (owner 2026-09-29) comes from the API
  // (spotbot's data): only a listed coin-config pair can be added. Missing
  // list = nothing can be added (never "everything").
  const tradeablePairs = possibleCoins.tradeablePairs ?? []
  const tradeable = new Set(tradeablePairs.map((p) => `${p.coin1}/${p.coin2}`))
  const isTradeable = (c1: string, c2: string) => tradeable.has(`${c1}/${c2}`)

  const normal = coins.filter((p) => isTradeable(p.coin1, p.coin2))
  const reverse = coins.filter((p) => isTradeable(p.coin2, p.coin1))
  const usdBases = [...new Set(coins.filter((p) => isUsd(p.coin2)).map((p) => p.coin1))]
  const virtualPairs = tradeablePairs.filter(
    (p) => usdBases.includes(p.coin1) && usdBases.includes(p.coin2) && !isUsd(p.coin2),
  )
  const virtualFirst = [...new Set(virtualPairs.map((p) => p.coin1))]
  const virtualSecond = [...new Set(virtualPairs.map((p) => p.coin2))]
  const offered: PairType[] = [
    ...(normal.length > 0 ? (['NCP'] as PairType[]) : []),
    ...(reverse.length > 0 ? (['RCP'] as PairType[]) : []),
    ...(virtualPairs.length > 0 ? (['VCP'] as PairType[]) : []),
  ]
  const type: PairType = offered.includes(typeState) ? typeState : (offered[0] ?? 'NCP')
  const options =
    type === 'NCP'
      ? normal
          .filter((p) => (coin2 === '-' || p.coin2 === coin2) && !handledPairs.has(p.coin1 + p.coin2))
          .filter((p) => !filter || (p.coin1 + p.coin2).includes(filter.toUpperCase()))
          .map((p) => ({ value: `${p.coin1}/${p.coin2}`, label: `${p.coin1}${p.coin2}` }))
      : reverse
          .filter((p) => (coin2 === '-' || p.coin2 === coin2) && !handledPairs.has(p.coin2 + p.coin1))
          .filter((p) => !filter || (p.coin2 + p.coin1).includes(filter.toUpperCase()))
          .map((p) => ({ value: `${p.coin2}/${p.coin1}`, label: `${p.coin2}${p.coin1}` }))

  function switchType(next: PairType) {
    setType(next)
    setNewCoin('')
    setFilter('')
    setVcp1('')
    setVcp2('')
  }

  function submit(event: FormEvent) {
    event.preventDefault()
    const pair = type === 'VCP' ? (vcp1 && vcp2 ? `${vcp1}/${vcp2}` : '') : newCoin
    const [c1, c2] = pair.split('/')
    if (!c1 || !c2) return
    onAdd(c1, c2)
    setNewCoin('')
    setVcp1('')
    setVcp2('')
  }

  const label = type === 'NCP' ? 'Add normal coin pair' : type === 'RCP' ? 'Add reverse coin pair' : 'Add virtual coin pair'
  const ready = type === 'VCP' ? Boolean(vcp1 && vcp2 && isTradeable(vcp1, vcp2)) : Boolean(newCoin)

  if (offered.length === 0) {
    return (
      <p className="portal-hint" data-testid="add-coin">
        No coin pair can be added on this exchange right now.
      </p>
    )
  }

  return (
    <form onSubmit={submit} data-testid="add-coin">
      <div className="portal-tabs portal-tabs--sub" role="tablist" aria-label="Coin pair type">
        {(
          [
            ['NCP', 'Normal Coin Pairs'],
            ['RCP', 'Reverse Coin Pairs'],
            ['VCP', 'Virtual Coin Pairs'],
          ] as [PairType, string][]
        )
          .filter(([value]) => offered.includes(value))
          .map(([value, text]) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={type === value}
            className={type === value ? 'portal-tab portal-tab--active' : 'portal-tab'}
            onClick={() => switchType(value)}
          >
            {text}
          </button>
        ))}
      </div>
      <div className="portal-inline-actions">
        {type !== 'VCP' ? (
          <>
            <select aria-label="Quote asset filter" value={coin2} onChange={(e) => onCoin2Change(e.target.value)}>
              <option value="-">all</option>
              {(possibleCoins.quoteAssets ?? []).map((q) => (
                <option key={q} value={q}>
                  {q}
                </option>
              ))}
            </select>
            <input
              type="search"
              placeholder="search..."
              aria-label="Search coin pairs"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
            <select aria-label="New coin pair" value={newCoin} onChange={(e) => setNewCoin(e.target.value)}>
              <option value="">select coin pair…</option>
              {options.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </>
        ) : (
          <>
            <select aria-label="Virtual coin 1" value={vcp1} onChange={(e) => setVcp1(e.target.value)}>
              <option value="">coin 1…</option>
              {virtualFirst
                .filter((c) => !vcp2 || (isTradeable(c, vcp2) && !handledPairs.has(c + vcp2)))
                .map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
            </select>
            <select aria-label="Virtual coin 2" value={vcp2} onChange={(e) => setVcp2(e.target.value)}>
              <option value="">coin 2…</option>
              {virtualSecond
                .filter((c) => !vcp1 || (isTradeable(vcp1, c) && !handledPairs.has(vcp1 + c)))
                .map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
            </select>
          </>
        )}
        <button type="submit" disabled={!ready || busy}>
          {busy ? 'Adding…' : label}
        </button>
      </div>
      <p className="portal-hint" data-testid="tradeable-pairs">
        Tradeable coin pairs: {tradeablePairs.map((p) => `${p.coin1}${p.coin2}`).join(', ')}
      </p>
    </form>
  )
}

function AddCoinToTag({
  coins,
  busy,
  onAdd,
  onBatch,
}: {
  coins: CoinConfig[]
  busy: boolean
  onAdd: (c: CoinConfig) => void
  onBatch: () => void
}) {
  const [value, setValue] = useState('')
  return (
    <div className="portal-grid-2">
      <div className="portal-inline-actions">
        <label>
          Add coin to this tag:{' '}
          <select aria-label="Add coin to this tag" value={value} onChange={(e) => setValue(e.target.value)}>
            <option value="" />
            {coins.map((c) => (
              <option key={c.coin1 + c.coin2} value={`${c.coin1}_${c.coin2}`}>
                {c.coin1}
                {c.coin2}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          disabled={!value || busy}
          onClick={() => {
            const [c1, c2] = value.split('_')
            const coin = coins.find((c) => c.coin1 === c1 && c.coin2 === c2)
            if (coin) onAdd(coin)
            setValue('')
          }}
        >
          Add
        </button>
      </div>
      <div>
        <button type="button" onClick={onBatch}>
          Batch modify coins
        </button>
      </div>
    </div>
  )
}

function TagModal({
  mode,
  initial,
  busy,
  onCancel,
  onSave,
}: {
  mode: 'add' | 'edit'
  initial: Partial<Tag>
  busy: boolean
  onCancel: () => void
  onSave: (tag: Partial<Tag>) => void
}) {
  const [tag, setTag] = useState<Partial<Tag>>(initial)
  return (
    <Modal open title={mode === 'add' ? 'Add new tag' : 'Edit tag'} onClose={onCancel}>
      <form
        className="portal-form"
        onSubmit={(e) => {
          e.preventDefault()
          if (tag.name) onSave(tag)
        }}
      >
        <label>
          Name
          <input value={tag.name ?? ''} onChange={(e) => setTag({ ...tag, name: e.target.value })} required />
        </label>
        <label>
          Description
          <textarea value={tag.description ?? ''} onChange={(e) => setTag({ ...tag, description: e.target.value })} />
        </label>
        <div className="portal-inline-actions">
          <button type="button" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" disabled={!tag.name || busy}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

function BatchModifyModal({
  tag,
  defaultConfig,
  busy,
  onCancel,
  onSave,
}: {
  tag: Tag
  defaultConfig: SpotDefaultConfig | null
  busy: boolean
  onCancel: () => void
  onSave: (body: Record<string, unknown>) => void
}) {
  const [accepted, setAccepted] = useState<string[]>([])
  const [values, setValues] = useState<Record<string, unknown>>({ bidConstant: '' })
  const [differentDown, setDifferentDown] = useState(false)
  const set = (patch: Record<string, unknown>) => setValues((prev) => ({ ...prev, ...patch }))

  // Legacy derived the constant NAMES from the default config (first part of
  // `NAME::ASSET`, de-duplicated).
  const bidNames = [...new Set((defaultConfig?.bidConstants ?? []).map((c) => bidConstantName(c.key)))]
  const gridNames = [...new Set((defaultConfig?.gridConstants ?? []).map((c) => bidConstantName(c.key)))]

  function reset() {
    setAccepted([])
    setValues({ bidConstant: '' })
    setDifferentDown(false)
  }

  function constantOrValue(constantKey: string, valueKey: string, names: string[], range: { min: number; max: number; step: number }, unit: string, label: string) {
    const constant = String(values[constantKey] ?? '')
    return (
      <span className="portal-input-group">
        <select
          aria-label={`${label} constant`}
          value={constant}
          onChange={(e) => set({ [constantKey]: e.target.value, [valueKey]: e.target.value ? -1 : null })}
        >
          {names.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
          <option value="">Fix value:</option>
        </select>
        {!constant && (
          <input
            type="number"
            aria-label={label}
            min={range.min}
            max={range.max}
            step={range.step}
            value={values[valueKey] === null || values[valueKey] === undefined ? '' : String(values[valueKey])}
            onChange={(e) => set({ [valueKey]: e.target.value === '' ? null : Number(e.target.value) })}
          />
        )}
        <span className="portal-input-unit">{unit}</span>
      </span>
    )
  }

  function editor(field: string) {
    switch (field) {
      case 'tradingMode':
        return (
          <>
            <select
              aria-label="Trading Mode"
              value={values.tradingMode === undefined ? '' : String(values.tradingMode)}
              onChange={(e) => set({ tradingMode: e.target.value === '' ? '' : Number(e.target.value) })}
            >
              <option value="" />
              <option value="-1">Inactive</option>
              <option value="0">Off</option>
              <option value="1">Buy and sell</option>
              <option value="2">Sell only</option>
              <option value="101">On (sets to Sell only)</option>
            </select>
            <span className="portal-hint">This command will not affect subscribed coin pairs!</span>
            {(values.tradingMode === 1 || values.tradingMode === 2) && (
              <span className="portal-hint">
                For coin pairs with Lock Off Trading Mode enabled, use &quot;On (sets to Sell only)&quot; first; to reach
                &quot;Buy and sell&quot;, run a second modify with that value.
              </span>
            )}
          </>
        )
      case 'bid':
        return (
          <>
            {constantOrValue('bidConstant', 'bid', bidNames, { min: 0.000001, max: 100000000, step: 0.000001 }, '', 'Bid')}
            <span className="portal-hint">This command will not affect subscribed coin pairs!</span>
          </>
        )
      case 'grid':
        return (
          <>
            {constantOrValue('gridUpConstant', 'gridUp', gridNames, { min: 0.1, max: 10, step: 0.1 }, '%', differentDown ? 'Grid Up' : 'Grid')}
            {differentDown && constantOrValue('gridDownConstant', 'gridDown', gridNames, { min: 0.1, max: 10, step: 0.1 }, '%', 'Grid Down')}
            <button type="button" onClick={() => setDifferentDown((v) => !v)}>
              {differentDown ? 'Use same value for Grid Down' : 'Set different value for Grid Down'}
            </button>
          </>
        )
      case 'lockOnOffMode':
      case 'offHandlerRunOnNonSameStrategyPositions':
        return (
          <select aria-label={BATCH_LABELS[field]} value={values[field] === undefined ? '' : String(values[field])} onChange={(e) => set({ [field]: Number(e.target.value) })}>
            <option value="" />
            <option value="-1">Use my default value</option>
            <option value="0">No</option>
            <option value="1">Yes</option>
          </select>
        )
      case 'maxMopCount':
        return (
          <select aria-label="Max MOP count" value={values.maxMopCount === undefined ? '' : String(values.maxMopCount)} onChange={(e) => set({ maxMopCount: Number(e.target.value) })}>
            <option value="" />
            <option value="-1">Use my default value</option>
            <option value="0">Unlimited</option>
            {MAX_MOP_COUNTS.map((n) => (
              <option key={n} value={String(n)}>
                {n}
              </option>
            ))}
          </select>
        )
      case 'offModeTrigger':
        return (
          <select aria-label="Off handler" value={values.offModeTrigger === undefined ? '' : String(values.offModeTrigger)} onChange={(e) => set({ offModeTrigger: Number(e.target.value) })}>
            <option value="" />
            <option value="-1">Use my default value</option>
            {[0, 1, 2, 3, 4, 5, 6, 11, 12, 13, 14, 15].map((t) => (
              <option key={t} value={String(t)}>
                {OFF_MODE_TRIGGERS[String(t)]}
              </option>
            ))}
          </select>
        )
      case 'autoBuyMode':
        return (
          <select aria-label="Auto Buy Mode" value={values.autoBuyMode === undefined ? '' : String(values.autoBuyMode)} onChange={(e) => set({ autoBuyMode: Number(e.target.value) })}>
            <option value="" />
            <option value="-1">Use my default value</option>
            {[0, 1, 2].map((m) => (
              <option key={m} value={String(m)}>
                {AUTO_BUY_MODES[String(m)]}
              </option>
            ))}
          </select>
        )
      case 'autoBuyAmount':
        return (
          <span className="portal-input-group">
            <input
              type="number"
              aria-label="Auto Buy Amount"
              min={0}
              max={1000}
              step={1}
              value={values.autoBuyAmount === undefined || values.autoBuyAmount === null ? '' : String(values.autoBuyAmount)}
              onChange={(e) => set({ autoBuyAmount: e.target.value === '' ? null : Number(e.target.value) })}
            />
            <span className="portal-input-unit">{PRIMARY_USD_ASSET}</span>
          </span>
        )
      default:
        return null
    }
  }

  return (
    <Modal open title={`Modify coins by tag: ${tag.name}`} onClose={onCancel} className="portal-modal-wide">
      <div data-testid="batch-modify">
        {accepted.map((field) => (
          <div key={field} className="portal-field-row">
            <strong>{BATCH_LABELS[field as (typeof BATCH_FIELDS)[number]]}</strong>
            <div className="portal-inline-actions">{editor(field)}</div>
            <button type="button" className="portal-button-danger" onClick={() => setAccepted(accepted.filter((f) => f !== field))}>
              Remove
            </button>
          </div>
        ))}
        <div className="portal-inline-actions">
          {BATCH_FIELDS.filter((f) => !accepted.includes(f)).map((field) => (
            <button key={field} type="button" onClick={() => setAccepted([...accepted, field])}>
              + {BATCH_LABELS[field]}
            </button>
          ))}
        </div>
        <div className="portal-form-footer portal-inline-actions">
          <button type="button" onClick={reset}>
            Remove all commands
          </button>
          <button type="button" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="submit"
            disabled={accepted.length === 0 || busy}
            onClick={() =>
              onSave(buildBatchModifyBody({ acceptedFields: accepted, setDifferentGridDown: differentDown, values }, PRIMARY_USD_ASSET))
            }
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  )
}
